import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useIntl } from "react-intl-compat";

import {
  UpdateSpacePayload,
  useUpdateSpaceMutation,
} from "../../../../components/src/services/spaces.service";
import type { Space } from "../../../../types/spaces.types";

import {
  buildArrivalGuideFormSchema,
  ArrivalGuideFormValues,
} from "./ArrivalGuideForm/ArrivalGuideForm.schema";

/**
 * Builds the form for the arrival-guide page (wifi instructions,
 * directions) and wires up its save action. Only the fields the host
 * actually touched are sent
 */
const useArrivalGuideForm = (space?: Space) => {
  const intl = useIntl();
  const [updateSpace] = useUpdateSpaceMutation();
  const [errorMsg, setErrorMsg] = React.useState<string>();

  const form = useForm<ArrivalGuideFormValues>({
    resolver: zodResolver(buildArrivalGuideFormSchema()),
    values: space
      ? {
          wifiInstructions: space.wifi_instructions ?? "",
          directions: space.getting_there ?? "",
        }
      : undefined,
  });

  const onSave = async (): Promise<boolean> => {
    setErrorMsg(undefined);
    let success = false;

    await form.handleSubmit(async (values) => {
      if (!space) {
        return;
      }

      const payload: UpdateSpacePayload = {
        wifi_instructions: values.wifiInstructions,
        getting_there: values.directions,
      };

      try {
        await updateSpace({ spaceId: space._id, payload }).unwrap();
        success = true;
      } catch {
        setErrorMsg(
          intl.formatMessage({
            id: "hosting.arrival_guide.submit_error",
            defaultMessage: "Something went wrong. Please try again.",
          }),
        );
      }
    })();

    return success;
  };

  return { form, onSave, errorMsg };
};

export { useArrivalGuideForm };
